// The seat drawer (Setup › Seats, `?seat=`): one seat with its active holders (`GET
// /seats/{id}`). Managers edit it (`PATCH`, If-Match = the version they saw; a 409 reloads the
// seat and says so), assign a person or a DE (`seat_occupied`, `supervisor_required` and
// `seat_closed` come back as inline problems), unassign a holder and close the seat; with `kpi`
// on, the seat's metric set and weights are edited here too. Responsibilities and their
// recurring work (M1-05) live here until the seat page (M1-08). A closed seat is read-only.
import {
  Badge,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerSection,
  DrawerTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Segmented,
  Switch,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import type { Role, Seat, SeatDetail } from "../../api/types";
import { actingAssignment, DeFillSwitch, HandBackDialog } from "../chart/de-seat-owner";
import { type CompanyAccess, isManager } from "../company";
import { formatDateTime } from "../format";
import { useReachConfirm } from "../memory/reach-confirm";
import { digitalEmployeesQuery, keys, peopleQuery, seatQuery, structureKeys } from "../queries";
import { Loading } from "../screen-parts";
import { useFeature } from "../use-feature";
import { SEAT_STATUS_LABELS, SEAT_STATUS_OPTIONS } from "./labels";
import { ConfirmButton, ErrorNotice, FormError, problemCode } from "./parts";
import { SeatResponsibilities } from "./responsibilities";
import { SeatMetricsEditor } from "./seat-metrics";
import { SEAT_STATUS_TONE } from "./seats-screen";
import { SeatTaskLimits } from "./task-limits";

/** What a save in the drawer changed (MODALS: the chart toasts it, and may close the drawer). */
export type SeatSave = "details" | "holder";

export function SeatDrawer({
  api,
  company,
  seatId,
  seats,
  roles,
  onClose,
  onSaved,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  /** The seats loaded on the page (names for "reports to"). */
  seats: Seat[];
  roles: Role[];
  onClose: () => void;
  /** After a successful save of the details or a holder assignment. */
  onSaved?: (what: SeatSave) => void;
}) {
  const seat = useQuery(seatQuery(api, company.id, seatId));
  const kpi = useFeature("kpi");
  return (
    <Drawer open onOpenChange={(open) => !open && onClose()}>
      <DrawerHeader
        closeLabel="Close seat details"
        actions={
          seat.data && seat.data.status !== "closed" ? (
            <CloseSeat api={api} co={company.id} seat={seat.data} />
          ) : null
        }
      >
        Setup › Seats
      </DrawerHeader>
      <DrawerBody data-slot="seat-drawer">
        {seat.isPending ? (
          <Loading label="Loading the seat" rows={4} />
        ) : seat.isError ? (
          <ErrorNotice what="The seat" error={seat.error} />
        ) : (
          <>
            <DrawerTitle>{seat.data.title}</DrawerTitle>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Badge variant={SEAT_STATUS_TONE[seat.data.status]}>
                {SEAT_STATUS_LABELS[seat.data.status]}
              </Badge>
              <span>{roles.find((r) => r.id === seat.data.role_id)?.name ?? "Role"}</span>
              <span aria-hidden="true">·</span>
              <span>Version {seat.data.version}</span>
            </div>
            {seat.data.status === "closed" ? (
              <Notice tone="neutral" className="mt-5">
                This seat is closed: its holders were unassigned and its keys revoked. Its history
                stays.
              </Notice>
            ) : null}
            <DrawerSection title="Holders" className="mt-6">
              <Holders api={api} company={company} seat={seat.data} onSaved={onSaved} />
            </DrawerSection>
            {seat.data.status !== "closed" ? (
              <DrawerSection title="Details">
                <SeatForm
                  key={`${seat.data.id}@${seat.data.version}`}
                  api={api}
                  co={company.id}
                  seat={seat.data}
                  seats={seats}
                  roles={roles}
                  onSaved={onSaved}
                />
              </DrawerSection>
            ) : null}
            {(() => {
              // TASK-BUDGETS: the DE holding this seat gets per-task limits here.
              const de = seat.data.assignments.find(
                (a) => a.membership_kind === "digital_employee",
              );
              return de && seat.data.status !== "closed" && isManager(company) ? (
                <DrawerSection title="Task limits">
                  <SeatTaskLimits
                    api={api}
                    co={company.id}
                    deId={de.membership_id}
                    readOnly={false}
                  />
                </DrawerSection>
              ) : null;
            })()}
            <DrawerSection title="Responsibilities">
              <SeatResponsibilities
                api={api}
                company={company}
                seatId={seat.data.id}
                readOnly={!isManager(company) || seat.data.status === "closed"}
              />
            </DrawerSection>
            {kpi.enabled ? (
              <DrawerSection title="Metrics and weights">
                <SeatMetricsEditor
                  api={api}
                  company={company}
                  seatId={seat.data.id}
                  readOnly={kpi.readOnly || seat.data.status === "closed"}
                />
              </DrawerSection>
            ) : null}
          </>
        )}
      </DrawerBody>
    </Drawer>
  );
}

function useSeatWrites(co: string, seatId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: structureKeys.seat(co, seatId) });
    void qc.invalidateQueries({ queryKey: keys.seats(co), exact: true });
    void qc.invalidateQueries({ queryKey: keys.digitalEmployees(co) });
    // MODALS: the drawer also opens over the chart, whose cards read the seat.
    void qc.invalidateQueries({ queryKey: ["company", co, "org-chart"] });
  };
}

function CloseSeat({ api, co, seat }: { api: Api; co: string; seat: SeatDetail }) {
  const refresh = useSeatWrites(co, seat.id);
  const close = useMutation({
    mutationFn: () => api.client.seats.close({ params: { co, id: seat.id } }),
    onSuccess: refresh,
  });
  return (
    <ConfirmButton
      label="Close seat"
      title={`Close "${seat.title}"?`}
      description="Its holders are unassigned and its ingest keys revoked, in one step. A closed seat cannot be reopened; its history and scorecards stay."
      confirmLabel="Close seat"
      pending={close.isPending}
      onConfirm={() => close.mutateAsync()}
    />
  );
}

function SeatForm({
  api,
  co,
  seat,
  seats,
  roles,
  onSaved,
}: {
  api: Api;
  co: string;
  seat: SeatDetail;
  seats: Seat[];
  roles: Role[];
  onSaved?: ((what: SeatSave) => void) | undefined;
}) {
  const refresh = useSeatWrites(co, seat.id);
  const [title, setTitle] = useState(seat.title);
  const [roleId, setRoleId] = useState(seat.role_id);
  const [supervisor, setSupervisor] = useState(seat.supervisor_seat_id ?? "");
  const [status, setStatus] = useState(seat.status);
  // CHART-STAFF: a staff (assistant) seat needs someone to report to; no supervisor, no staff.
  const [staff, setStaff] = useState(seat.staff);
  const staffNow = staff && supervisor !== "";
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api.client.seats.update({
        params: { co, id: seat.id },
        ifMatch: seat.version,
        body: {
          ...(title.trim() !== seat.title ? { title: title.trim() } : {}),
          ...(roleId !== seat.role_id ? { role_id: roleId } : {}),
          ...((supervisor || null) !== seat.supervisor_seat_id
            ? { supervisor_seat_id: supervisor || null }
            : {}),
          ...(staffNow !== seat.staff ? { staff: staffNow } : {}),
          ...(status !== seat.status && status !== "closed" && status !== "proposed"
            ? { status }
            : {}),
        },
      }),
    onSuccess: () => {
      setSaved(true);
      refresh();
      onSaved?.("details");
    },
    onError: (e) => {
      // Someone else changed the seat: reload it so the next save sends its new version.
      if (problemCode(e) === "conflict") refresh();
    },
  });
  const dirty =
    title.trim() !== seat.title ||
    roleId !== seat.role_id ||
    (supervisor || null) !== seat.supervisor_seat_id ||
    staffNow !== seat.staff ||
    status !== seat.status;
  const candidates = seats.filter((s) => s.id !== seat.id && s.status !== "closed");
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (dirty && title.trim()) save.mutate();
  };
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" aria-label="Seat details">
      <Field>
        <FieldLabel htmlFor="seat-title">Title</FieldLabel>
        <Input
          id="seat-title"
          value={title}
          required
          onChange={(e) => {
            setSaved(false);
            setTitle(e.target.value);
          }}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field>
          <FieldLabel htmlFor="seat-role">Role</FieldLabel>
          <NativeSelect
            id="seat-role"
            value={roleId}
            onChange={(e) => {
              setSaved(false);
              setRoleId(e.target.value);
            }}
          >
            {roles.some((r) => r.id === roleId) ? null : (
              <NativeSelectOption value={roleId}>Current role</NativeSelectOption>
            )}
            {roles.map((r) => (
              <NativeSelectOption key={r.id} value={r.id}>
                {r.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <Field>
          <FieldLabel htmlFor="seat-supervisor">Reports to</FieldLabel>
          <NativeSelect
            id="seat-supervisor"
            value={supervisor}
            onChange={(e) => {
              setSaved(false);
              setSupervisor(e.target.value);
            }}
          >
            <NativeSelectOption value="">No one</NativeSelectOption>
            {candidates.map((s) => (
              <NativeSelectOption key={s.id} value={s.id}>
                {s.title}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <Field>
          <FieldLabel htmlFor="seat-status">Status</FieldLabel>
          <NativeSelect
            id="seat-status"
            value={status}
            onChange={(e) => {
              setSaved(false);
              setStatus(e.target.value as Seat["status"]);
            }}
          >
            {SEAT_STATUS_OPTIONS.map((o) => (
              <NativeSelectOption key={o.value} value={o.value}>
                {o.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
      </div>
      <div className="flex items-center gap-2" data-slot="seat-staff-toggle">
        <Switch
          id="seat-staff"
          checked={staffNow}
          disabled={supervisor === ""}
          onCheckedChange={(on) => {
            setSaved(false);
            setStaff(on);
          }}
        />
        <Label htmlFor="seat-staff">Staff role (assistant)</Label>
        <InfoTip label="About staff roles">
          An assistant to the seat it reports to, such as a Chief of Staff. The chart draws it
          beside that seat's card, not with the line reports. Approvals and escalations still go to
          that seat.
        </InfoTip>
      </div>
      {problemCode(save.error) === "conflict" ? (
        <Notice tone="warning" role="alert">
          Someone changed this seat while you were editing; it has been reloaded. Check it and save
          again.
        </Notice>
      ) : (
        <FormError what="Saving the seat" error={save.error} />
      )}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={!dirty || !title.trim() || save.isPending}>
          {save.isPending ? "Saving…" : "Save seat"}
        </Button>
        <span role="status" className="text-sm text-muted-foreground">
          {saved && !dirty ? "Seat saved." : ""}
        </span>
      </div>
    </form>
  );
}

type HolderKind = "person" | "digital_employee";

function Holders({
  api,
  company,
  seat,
  onSaved,
}: {
  api: Api;
  company: CompanyAccess;
  seat: SeatDetail;
  onSaved?: ((what: SeatSave) => void) | undefined;
}) {
  const people = useInfiniteQuery(peopleQuery(api, company.id));
  const des = useInfiniteQuery(digitalEmployeesQuery(api, company.id));
  const refresh = useSeatWrites(company.id, seat.id);
  const personList = people.data?.pages.flatMap((p) => p.data) ?? [];
  const deList = des.data?.pages.flatMap((p) => p.data) ?? [];
  const nameOf = (id: string) =>
    personList.find((p) => p.id === id)?.display_name ??
    workerName(
      deList.find((d) => d.membership_id === id)?.display_name ?? `Member …${id.slice(-4)}`,
    );
  const isDe = (id: string) => deList.some((d) => d.membership_id === id);
  const holderKinds = new Set(seat.assignments.map((a) => a.membership_kind));
  const open = seat.status !== "closed";
  const [kind, setKind] = useState<HolderKind>(
    holderKinds.has("person") ? "digital_employee" : "person",
  );
  const [member, setMember] = useState("");
  const options =
    kind === "person"
      ? personList
          .filter((p) => p.status === "active")
          .map((p) => ({
            id: p.id,
            label: p.email ? `${p.display_name} · ${p.email}` : p.display_name,
          }))
      : deList.map((d) => ({
          id: d.membership_id,
          label: d.seat
            ? `${workerName(d.display_name)} · now on ${d.seat.title}`
            : workerName(d.display_name),
        }));
  const assign = useMutation({
    mutationFn: () =>
      api.client.seats.assign({
        params: { co: company.id, id: seat.id },
        body: { membership_id: member },
      }),
    onSuccess: () => {
      setMember("");
      refresh();
      onSaved?.("holder");
    },
  });
  const unassign = useMutation({
    mutationFn: (membership: string) =>
      api.client.seats.unassign({
        params: { co: company.id, id: seat.id },
        body: { membership_id: membership },
      }),
    onSuccess: refresh,
  });
  // D8: a DE given this seat is first shown what it will read in memory after
  const { confirmReach, reachDialog } = useReachConfirm(api, company.id);
  const onAssign = async (e: FormEvent) => {
    e.preventDefault();
    if (!member) return;
    if (
      kind === "digital_employee" &&
      !(await confirmReach({
        kind: "seat_holder",
        membership_id: member,
        add_seat_id: seat.id,
        end_seat_id: null,
      }))
    )
      return;
    if (kind === "person" && actingAssignment(seat)) {
      setHandBack(true);
      return;
    }
    assign.mutate();
  };
  const assignProblem = problemCode(assign.error);
  // DE-SEAT-OWNER: a person assigned to a seat a DE is acting in is a hand-back, confirmed first.
  const acting = actingAssignment(seat);
  const [handBack, setHandBack] = useState(false);
  const handBackTo = personList.find((p) => p.id === member)?.display_name ?? "the person";
  return (
    <div className="flex flex-col gap-4">
      {reachDialog}
      {acting ? (
        <HandBackDialog
          open={handBack}
          deName={nameOf(acting.membership_id)}
          personName={handBackTo}
          since={formatDateTime(acting.active_from)}
          pending={assign.isPending}
          onCancel={() => setHandBack(false)}
          onConfirm={() => {
            setHandBack(false);
            assign.mutate();
          }}
        />
      ) : null}
      {seat.assignments.some((a) => a.membership_kind === "digital_employee") ? (
        <DeFillSwitch api={api} company={company} seat={seat} canEdit={isManager(company)} />
      ) : null}
      <Table data-slot="seat-holders">
        <TableCaption>Who holds this seat now: at most one person and one DE.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Holder</TableHead>
            <TableHead scope="col">Kind</TableHead>
            <TableHead scope="col">Since</TableHead>
            {open ? (
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {seat.assignments.length ? (
            seat.assignments.map((a) => (
              <TableRow key={a.id} data-holder={a.membership_id}>
                <TableHead scope="row">
                  {isDe(a.membership_id) ? (
                    <span className="flex items-center gap-1.5">
                      <WorkerBadge kind="de" size="sm" />
                      {nameOf(a.membership_id)}
                    </span>
                  ) : (
                    nameOf(a.membership_id)
                  )}
                </TableHead>
                <TableCell>
                  {a.membership_kind === "person" ? "Person" : "Digital employee"}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDateTime(a.active_from)}
                </TableCell>
                {open ? (
                  <TableCell className="text-right">
                    <ConfirmButton
                      label="Unassign"
                      srLabel={nameOf(a.membership_id)}
                      title={`Unassign ${nameOf(a.membership_id)}?`}
                      description={
                        a.membership_kind === "person"
                          ? "They lose this seat's access. A DE that reports to this seat needs a new supervisor."
                          : "The DE leaves the seat and its ingest keys for this seat are revoked."
                      }
                      confirmLabel="Unassign"
                      pending={unassign.isPending}
                      onConfirm={() => unassign.mutateAsync(a.membership_id)}
                    />
                  </TableCell>
                ) : null}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={open ? 4 : 3} className="text-muted-foreground">
                Nobody holds this seat.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {open ? (
        <form onSubmit={onAssign} className="flex flex-col gap-3" aria-label="Assign a holder">
          <div className="flex flex-wrap items-end gap-3">
            <Segmented
              aria-label="Holder kind"
              size="sm"
              value={kind}
              onValueChange={(k) => {
                setKind(k);
                setMember("");
              }}
              options={[
                { value: "person", label: "Person" },
                { value: "digital_employee", label: "Digital employee" },
              ]}
            />
            <Field className="min-w-56 flex-1">
              <FieldLabel htmlFor="seat-assign-member">
                {kind === "person" ? "Person" : "Digital employee"}
              </FieldLabel>
              <NativeSelect
                id="seat-assign-member"
                value={member}
                onChange={(e) => setMember(e.target.value)}
              >
                <NativeSelectOption value="">Choose…</NativeSelectOption>
                {options.map((o) => (
                  <NativeSelectOption key={o.id} value={o.id}>
                    {o.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Button type="submit" disabled={!member || assign.isPending}>
              {assign.isPending ? "Assigning…" : "Assign"}
            </Button>
          </div>
          {holderKinds.has(kind) ? (
            <p className="text-xs text-muted-foreground">
              This seat already has a {kind === "person" ? "person" : "DE"}: unassign them first.
            </p>
          ) : null}
          {assignProblem === "supervisor_required" ? (
            <Notice tone="warning" role="alert" title="This seat needs a staffed supervisor">
              A digital employee can only hold a seat that reports to a seat held by a person. Set
              "Reports to" below and staff that seat first.
            </Notice>
          ) : assignProblem === "seat_occupied" ? (
            <Notice tone="warning" role="alert" title="The seat is taken">
              It already has an active {kind === "person" ? "person" : "DE"}. Unassign them first.
            </Notice>
          ) : (
            <FormError what="Assigning" error={assign.error} />
          )}
          {people.hasNextPage || des.hasNextPage ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="self-start"
              onClick={() =>
                void (kind === "person" ? people.fetchNextPage() : des.fetchNextPage())
              }
            >
              Load more {kind === "person" ? "people" : "digital employees"}
            </Button>
          ) : null}
        </form>
      ) : null}
    </div>
  );
}
